import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faEdit, 
    faTrash, 
    faImage,
    faSpinner,
    faChevronLeft,
    faChevronRight,
    faTimes,
    faSortUp,
    faSortDown,
    faSort,
    faPlusCircle
} from '@fortawesome/free-solid-svg-icons';
import { faExpand, faCompress, faCog, faFilter, faCheck, faBroom } from '@fortawesome/free-solid-svg-icons';
import util from '../../utils/miniUtils';
import { MECHANIC_USER, PROVIDER_USER } from '../../utils/constants';
// import Swal from 'sweetalert2';
import './InventoryTable.css';

// Default column definition (keys used in data rows)
const DEFAULT_COLUMNS = [
    { key: 'select', label: '', width: '40px', forced: true },
    { key: 'actions', label: 'Acciones', width: '50px' },
    { key: 'image', label: 'Imagen', width: '60px' },
    { key: 'id', label: 'ID', sortable: true, width: '80px', forced: true },
    { key: 'article', label: 'Artículo', sortable: true, minWidth: '135px', forced: true },
    { key: 'description', label: 'Descripción', sortable: true, minWidth: '250px' },
    { key: 'categoryName', label: 'Categoría', sortable: true, width: '140px' },
    { key: 'brand', label: 'Marca', sortable: true, width: '100px' },
    { key: 'model', label: 'Modelo', sortable: true, width: '100px' },
    { key: 'year', label: 'Año', sortable: true, width: '80px' },
    { key: 'createdBy', label: 'Creado Por', width: '80px' },
    { key: 'qty', label: 'Cantidad', sortable: true, width: '80px' },
    { key: 'locationName', label: 'Ubicación', sortable: true, width: '100px' },
    { key: 'price', label: 'Precio', sortable: true, width: '100px' }
];

const MOBILE_MAX_WIDTH = 640;

const InventoryTable = forwardRef( ( { baseUrl = process.env.REACT_APP_BASE_URL, authToken = '', onAction = null, onRowAction = null, onError = null, onCreateProduct = null }, ref ) => {
    // Estados principales
    const [ data, setData ] = useState( [] );
    const [ loading, setLoading ] = useState( false );
    const [ error, setError ] = useState( null );
    const [ totalCount, setTotalCount ] = useState( 0 );
    const [ selectedRows, setSelectedRows ] = useState( [] );

    // Estados de paginación y ordenamiento
    const [ currentPage, setCurrentPage ] = useState( 0 );
    const [ pageSize, setPageSize ] = useState( 100 );
    const [ orderBy, setOrderBy ] = useState( 'id' );
    const [ direction, setDirection ] = useState( 'desc' );

    // Estados de filtros
    const [ filters, setFilters ] = useState( {
        search: '',
        category_id: '',
        brand_id: '',
        model_id: '',
        size_id: '',
        year: '',
        location_id: ''
    } );

    // Estados de catálogos
    const [ catalogs, setCatalogs ] = useState( {
        categories: [],
        brands: [],
        models: [],
        sizes: []
    } );

    // Estado para ubicaciones/sucursales
    const [ locations, setLocations ] = useState( [] );

    // Referencias para debounce y cálculo de dimensiones
    const searchTimeoutRef = useRef( null );
    const containerRef = useRef( null );
    const filtersRef = useRef( null );
    const paginationRef = useRef( null );
    const tableRef = useRef( null );

    const [ isFullscreen, setIsFullscreen ] = useState( false );
    const [ tableBodyHeight, setTableBodyHeight ] = useState( 600 );
    const [ columns, setColumns ] = useState( [] );
    const [ showColumnsModal, setShowColumnsModal ] = useState( false );
    const [ viewportWidth, setViewportWidth ] = useState( typeof window !== 'undefined' ? window.innerWidth : 1024 );
    const [ showFiltersModal, setShowFiltersModal ] = useState( false );

    const isMobile = viewportWidth <= MOBILE_MAX_WIDTH;
    
    // Role-based access control: Mechanic (3) and Provider (4) are read-only
    const userRole = parseInt( util.user.role || '1' );
    const canWrite = ![ MECHANIC_USER, PROVIDER_USER ].includes( userRole );

    // localStorage key helpers (per-browser unique key)
    const USER_UUID_KEY = 'krs_inventory_user_uuid_v1';
    const CONFIG_KEY_PREFIX = 'krs_inventory_table_config_';

    const getOrCreateUuid = () => {
        try {
            let uuid = localStorage.getItem( USER_UUID_KEY );
            if ( !uuid ) {
                // Prefer native crypto.randomUUID when available
                uuid = ( typeof window !== 'undefined' && window.crypto && window.crypto.randomUUID ) ? window.crypto.randomUUID() : `uuid-${Date.now()}-${Math.floor( Math.random() * 100000 )}`;
                localStorage.setItem( USER_UUID_KEY, uuid );
            }
            return uuid;
        } catch ( e ) {
            return null;
        }
    };

    const getStorageKey = () => {
        const uuid = getOrCreateUuid();
        return uuid ? `${CONFIG_KEY_PREFIX}${uuid}` : `${CONFIG_KEY_PREFIX}anonymous`;
    };

    const loadColumnsFromStorage = () => {
        try {
            const key = getStorageKey();
            const raw = localStorage.getItem( key );
            if ( raw ) {
                const parsed = JSON.parse( raw );
                // Basic validation
                if ( Array.isArray( parsed ) ) return parsed;
            }
        } catch ( e ) { /* ignore */ }
        // Deep copy default
        return JSON.parse( JSON.stringify( DEFAULT_COLUMNS ) );
    };

    const saveColumnsToStorage = ( cols ) => {
        try {
            const key = getStorageKey();
            localStorage.setItem( key, JSON.stringify( cols ) );
        } catch ( e ) { /* ignore */ }
    };

    // Función para generar años
    const generateYears = useCallback( () => {
        const years = [];
        const currentYear = new Date().getFullYear();
        for ( let year = 1980; year <= currentYear; year++ ) {
            years.push( year.toString() );
        }
        return years;
    }, [] );

    // Configuración de axios
    const getAxiosConfig = useCallback( () => ( {
        headers: { Authorization: authToken }
    } ), [ authToken ] );

    // Cargar catálogos
    useEffect( () => {
        const loadCatalogs = async () => {
            try {
                const config = {
                    headers: { Authorization: authToken }
                };
                const response = await axios.get( `${baseUrl}/catalog/data/getSelectorsData`, config );

                if ( response.data.error ) {
                    throw new Error( response.data.msg || 'Error al cargar catálogos' );
                }

                const catalogData = response.data.data || response.data || {};
                
                // Si catalogData es un array, separar por tipo de catálogo
                let categories = [];
                let brands = [];
                let models = [];
                let sizes = [];
                
                if ( Array.isArray( catalogData ) ) {
                    categories = catalogData.filter( item => item.catalog === 'category' && item.status === 1 );
                    brands = catalogData.filter( item => item.catalog === 'brand' && item.status === 1 );
                    models = catalogData.filter( item => item.catalog === 'model' && item.status === 1 );
                    sizes = catalogData.filter( item => item.catalog === 'size' && item.status === 1 );
                    // console.log( 'Sizes filtrados:', sizes );
                } else {
                    // Estructura anterior por si acaso
                    categories = catalogData.categories || [];
                    brands = catalogData.brands || [];
                    models = catalogData.models || [];
                    sizes = catalogData.sizes || [];
                }
                
                setCatalogs( {
                    categories,
                    brands,
                    models,
                    sizes
                } );
                // console.log( 'Estructura completa de respuesta:', response.data );
                // console.log( 'Catálogos procesados:', { categories, brands, models, sizes } );
            } catch ( err ) {
                console.error( 'Error loading catalogs:', err );
                console.error( 'Error details:', err.response?.data );
                setError( 'Error al cargar catálogos: ' + err.message );
                // Set empty catalogs as fallback
                setCatalogs( {
                    categories: [],
                    brands: [],
                    models: [],
                    sizes: []
                } );
            }
        };

        if ( baseUrl && authToken ) {
            loadCatalogs();
        }
    }, [ baseUrl, authToken ] );

    // Cargar ubicaciones/sucursales
    useEffect( () => {
        const loadLocations = async () => {
            try {
                const config = {
                    headers: { Authorization: authToken }
                };
                const response = await axios.get( `${baseUrl}/locations/data/list`, config );

                if ( response.data.error ) {
                    throw new Error( response.data.msg || 'Error al cargar ubicaciones' );
                }

                const locationsData = response.data.data || [];
                setLocations( locationsData );
            } catch ( err ) {
                console.error( 'Error loading locations:', err );
                // Set empty locations as fallback
                setLocations( [] );
            }
        };

        if ( baseUrl && authToken ) {
            loadLocations();
        }
    }, [ baseUrl, authToken ] );

    // Función para cargar datos
    const loadData = useCallback( async () => {
        if ( !baseUrl || !authToken ) return;

        setLoading( true );
        setError( null );

        try {
            const config = getAxiosConfig();
            const params = {
                page: currentPage,
                rows: pageSize,
                orderBy,
                direction,
                ...filters
            };

            const response = await axios.get( `${baseUrl}/product/read`, {
                ...config,
                params
            } );

            if ( response.data.error ) {
                throw new Error( response.data.msg || 'Error al cargar datos' );
            }

            setData( response.data.data || [] );
            setTotalCount( response.data.total || 0 );
        } catch ( err ) {
            console.error( 'Error loading data:', err );
            setError( err.message );
        } finally {
            setLoading( false );
        }
    }, [ baseUrl, authToken, currentPage, pageSize, orderBy, direction, filters, getAxiosConfig ] );

    // Cargar datos cuando cambien las dependencias
    useEffect( () => {
        loadData();
    }, [ loadData ] );

    // Exponer métodos para el componente padre
    useImperativeHandle( ref, () => ( {
        reloadData: loadData,
        clearSelection: () => setSelectedRows( [] )
    } ), [ loadData ] );

    // Manejar errores
    useEffect( () => {
        if ( error && onError ) {
            onError( new Error( error ) );
        }
    }, [ error, onError ] );

    // Manejo de búsqueda con debounce
    const handleSearchChange = useCallback( ( value ) => {
        if ( searchTimeoutRef.current ) {
            clearTimeout( searchTimeoutRef.current );
        }

        searchTimeoutRef.current = setTimeout( () => {
            setFilters( prev => ( { ...prev, search: value } ) );
            setCurrentPage( 0 );
        }, 300 );
    }, [] );

    // Manejo de cambio de filtro
    const handleFilterChange = useCallback( ( field, value ) => {
        setFilters( prev => ( { ...prev, [ field ]: value } ) );
        setCurrentPage( 0 );
    }, [] );

    // Manejo de ordenamiento
    const handleSort = useCallback( ( column ) => {
        if ( orderBy === column ) {
            setDirection( prev => prev === 'asc' ? 'desc' : 'asc' );
        } else {
            setOrderBy( column );
            setDirection( 'asc' );
        }
        setCurrentPage( 0 );
    }, [ orderBy ] );

    // Manejo de selección
    const handleSelectAll = useCallback( ( checked ) => {
        if ( checked ) {
            setSelectedRows( data.map( item => item.id ) );
        } else {
            setSelectedRows( [] );
        }
    }, [ data ] );

    const handleSelectRow = useCallback( ( id, checked ) => {
        if ( checked ) {
            setSelectedRows( prev => [ ...prev, id ] );
        } else {
            setSelectedRows( prev => prev.filter( rowId => rowId !== id ) );
        }
    }, [] );

    // Funciones de acción masiva
    // (Bulk edit removed - not used)
    const handleBulkActivate = useCallback( async () => {
        if ( selectedRows.length === 0 ) return;

        // const result = await Swal.fire( {
        //     title: '¿Dar de alta los elementos seleccionados?',
        //     text: `Se marcarán como activos ${selectedRows.length} elementos`,
        //     icon: 'question',
        //     showCancelButton: true,
        //     confirmButtonText: 'Sí, dar de alta',
        //     cancelButtonText: 'Cancelar'
        // } );

        // if ( result.isConfirmed && onAction ) {
        onAction( 'bulkActivate', selectedRows );
        // }
    }, [ selectedRows, onAction ] );

    const handleBulkDeactivate = useCallback( async () => {
        if ( selectedRows.length === 0 ) return;

        // const result = await Swal.fire( {
        //     title: '¿Dar de baja los elementos seleccionados?',
        //     text: `Se marcarán como inactivos ${selectedRows.length} elementos`,
        //     icon: 'warning',
        //     showCancelButton: true,
        //     confirmButtonText: 'Sí, dar de baja',
        //     cancelButtonText: 'Cancelar',
        //     confirmButtonColor: '#d33'
        // } );

        // if ( result.isConfirmed && onAction ) {
        onAction( 'bulkDeactivate', selectedRows );
        // }
    }, [ selectedRows, onAction ] );

    const handleBulkDelete = useCallback( async () => {
    // console.log( 'Intentando eliminar filas:', selectedRows );
        if ( selectedRows.length === 0 ) return;

        // const result = await Swal.fire( {
        //     title: '¿Eliminar elementos seleccionados?',
        //     text: `Se eliminarán ${selectedRows.length} elementos. Esta acción no se puede deshacer.`,
        //     icon: 'warning',
        //     showCancelButton: true,
        //     confirmButtonText: 'Sí, eliminar',
        //     cancelButtonText: 'Cancelar',
        //     confirmButtonColor: '#d33'
        // } );

        // if ( result.isConfirmed && onAction ) {
        onAction( 'bulkDelete', selectedRows );
        // }
    }, [ selectedRows, onAction ] );

    // Cálculos de paginación
    const totalPages = Math.ceil( totalCount / pageSize );
    const startIndex = currentPage * pageSize;
    const endIndex = Math.min( startIndex + pageSize, totalCount );

    const getSortIcon = ( column ) => {
        if ( orderBy !== column ) return faSort;
        return direction === 'asc' ? faSortUp : faSortDown;
    };

    const renderFilters = () => (
        <div className="inventory-table-filters" ref={filtersRef}>
            <div className="row align-items-end">
                <div className="col-md-2">
                    <label className="form-label small">Buscar</label>
                    <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Buscar productos..."
                        onChange={( e ) => handleSearchChange( e.target.value )}
                    />
                </div>
                <div className="col-md-2">
                    <label className="form-label small">Categoría</label>
                    <select
                        className="form-select form-select-sm"
                        value={filters.category_id}
                        onChange={( e ) => handleFilterChange( 'category_id', e.target.value )}
                    >
                        <option value="">Todas</option>
                        {catalogs.categories.map( cat => (
                            <option key={cat.id} value={cat.id}>
                                {cat.value}
                            </option>
                        ) )}
                    </select>
                </div>
                <div className="col-md-2">
                    <label className="form-label small">Marca</label>
                    <select
                        className="form-select form-select-sm"
                        value={filters.brand_id}
                        onChange={( e ) => handleFilterChange( 'brand_id', e.target.value )}
                    >
                        <option value="">Todas</option>
                        {catalogs.brands.map( brand => (
                            <option key={brand.id} value={brand.id}>
                                {brand.value}
                            </option>
                        ) )}
                    </select>
                </div>
                <div className="col-md-2">
                    <label className="form-label small">Modelo</label>
                    <select
                        className="form-select form-select-sm"
                        value={filters.model_id}
                        onChange={( e ) => handleFilterChange( 'model_id', e.target.value )}
                    >
                        <option value="">Todos</option>
                        {catalogs.models.map( model => (
                            <option key={model.id} value={model.id}>
                                {model.value}
                            </option>
                        ) )}
                    </select>
                </div>
                <div className="col-md-1">
                    <label className="form-label small">Tamaño</label>
                    <select
                        className="form-select form-select-sm"
                        value={filters.size_id}
                        onChange={( e ) => handleFilterChange( 'size_id', e.target.value )}
                    >
                        <option value="">Todos</option>
                        {catalogs.sizes.map( size => (
                            <option key={size.id} value={size.id}>
                                {size.value}
                            </option>
                        ) )}
                    </select>
                </div>
                <div className="col-md-1">
                    <label className="form-label small">Año</label>
                    <select
                        className="form-select form-select-sm"
                        value={filters.year}
                        onChange={( e ) => handleFilterChange( 'year', e.target.value )}
                    >
                        <option value="">Todos</option>
                        {generateYears().map( year => (
                            <option key={year} value={year}>
                                {year}
                            </option>
                        ) )}
                    </select>
                </div>
                <div className="col-md-2">
                    <label className="form-label small">Sucursal</label>
                    <select
                        className="form-select form-select-sm"
                        value={filters.location_id}
                        onChange={( e ) => handleFilterChange( 'location_id', e.target.value )}
                    >
                        <option value="">Todas</option>
                        {locations.map( location => (
                            <option key={location.id} value={location.id}>
                                {location.name}
                            </option>
                        ) )}
                    </select>
                </div>
                <div className="col-md-1 d-flex">
                    {canWrite && (
                        <button
                            type="button"
                            className="btn btn-primary btn-sm ms-2"
                            onClick={() => onCreateProduct && onCreateProduct()}
                            title="Crear nuevo producto"
                        >
                            <FontAwesomeIcon icon={faPlusCircle} />
                        </button>
                    )}
                    <button
                        type="button"
                        className="btn btn-secondary btn-sm ms-2"
                        onClick={() => {
                            setFilters( {
                                search: '',
                                category_id: '',
                                brand_id: '',
                                model_id: '',
                                size_id: '',
                                year: '',
                                location_id: ''
                            } );
                            setCurrentPage( 0 );
                        }}
                    >
                        <FontAwesomeIcon icon={faTimes} title="Limpiar" aria-label="Limpiar filtros" />
                    </button>

                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm ms-2"
                        title="Configurar columnas"
                        onClick={() => setShowColumnsModal( true )}
                    >
                        <FontAwesomeIcon icon={faCog} />
                    </button>

                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm ms-2"
                        title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
                        onClick={() => setIsFullscreen( prev => !prev )}
                    >
                        <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} />
                    </button>
                </div>
            </div>
        </div>
    );

    const renderToolbar = () => {
        if ( selectedRows.length === 0 || !canWrite ) return null;

        return (
            <div className="inventory-table-toolbar">
                <span className="me-3">{selectedRows.length} seleccionados</span>
                <div className="btn-group btn-group-sm">
                    <button
                        type="button"
                        className="btn btn-outline-light btn-sm"
                        onClick={handleBulkActivate}
                        title="Dar de Alta"
                    >
                        Dar de Alta
                    </button>
                    <button
                        type="button"
                        className="btn btn-outline-light btn-sm"
                        onClick={handleBulkDeactivate}
                        title="Dar de Baja"
                    >
                        Dar de Baja
                    </button>
                    <button
                        type="button"
                        className="btn btn-outline-light btn-sm"
                        onClick={handleBulkDelete}
                        title="Eliminar"
                    >
                        <FontAwesomeIcon icon={faTrash} /> Eliminar
                    </button>
                </div>
            </div>
        );
    };

    // legacy renderers removed; using dynamic renderers

    // legacy renderers removed; using dynamic renderers

    const renderPagination = () => {
        const maxVisiblePages = 5;
        const startPage = Math.max( 0, currentPage - Math.floor( maxVisiblePages / 2 ) );
        const endPage = Math.min( totalPages - 1, startPage + maxVisiblePages - 1 );

        return (
            <div className="inventory-table-pagination">
                <div className="d-flex justify-content-between align-items-center">
                    <div className="inventory-table-info">
                        Mostrando {startIndex + 1} a {endIndex} de {totalCount} elementos
                    </div>
                    
                    <div className="d-flex align-items-center gap-3">
                        <div className="d-flex align-items-center">
                            {/* Scroll to top button */}
                            <button
                                type="button"
                                className="btn btn-light btn-sm me-2"
                                title="Ir al inicio de la tabla"
                                onClick={() => {
                                    try {
                                        if ( tableRef && tableRef.current ) {
                                            const tbody = tableRef.current.querySelector( 'tbody' );
                                            if ( tbody ) {
                                                tbody.scrollIntoView( { behavior: 'smooth', block: 'start' } );
                                            } else {
                                                tableRef.current.scrollIntoView( { behavior: 'smooth', block: 'start' } );
                                            }
                                        }
                                    } catch ( e ) {
                                        console.error( 'Scroll to top error:', e );
                                    }
                                }}
                            >
                                <span className='ml-2'>Ir Arriba</span>
                            </button>
                            <label className="form-label me-2 mb-0">Mostrar:</label>
                            <select
                                className="form-select form-select-sm"
                                style={{ width: 'auto' }}
                                value={pageSize}
                                onChange={( e ) => {
                                    setPageSize( parseInt( e.target.value ) );
                                    setCurrentPage( 0 );
                                }}
                            >
                                <option value={100}>100</option>
                                <option value={150}>150</option>
                                <option value={200}>200</option>
                            </select>
                        </div>

                        <nav>
                            <ul className="pagination pagination-sm mb-0">
                                <li className={`page-item ${currentPage === 0 ? 'disabled' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => setCurrentPage( 0 )}
                                        disabled={currentPage === 0}
                                    >
                                        <FontAwesomeIcon icon={faChevronLeft} />
                                        <FontAwesomeIcon icon={faChevronLeft} />
                                    </button>
                                </li>
                                <li className={`page-item ${currentPage === 0 ? 'disabled' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => setCurrentPage( currentPage - 1 )}
                                        disabled={currentPage === 0}
                                    >
                                        <FontAwesomeIcon icon={faChevronLeft} />
                                    </button>
                                </li>
                                
                                {Array.from( { length: endPage - startPage + 1 }, ( _, i ) => {
                                    const pageNum = startPage + i;
                                    return (
                                        <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                                            <button
                                                className="page-link"
                                                onClick={() => setCurrentPage( pageNum )}
                                            >
                                                {pageNum + 1}
                                            </button>
                                        </li>
                                    );
                                } )}
                                
                                <li className={`page-item ${currentPage >= totalPages - 1 ? 'disabled' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => setCurrentPage( currentPage + 1 )}
                                        disabled={currentPage >= totalPages - 1}
                                    >
                                        <FontAwesomeIcon icon={faChevronRight} />
                                    </button>
                                </li>
                                <li className={`page-item ${currentPage >= totalPages - 1 ? 'disabled' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => setCurrentPage( totalPages - 1 )}
                                        disabled={currentPage >= totalPages - 1}
                                    >
                                        <FontAwesomeIcon icon={faChevronRight} />
                                        <FontAwesomeIcon icon={faChevronRight} />
                                    </button>
                                </li>
                            </ul>
                        </nav>
                    </div>
                </div>
            </div>
        );
    };

    // Calcular altura disponible para el body de la tabla
    useEffect( () => {
        const calculate = () => {
            try {
                const top = containerRef.current ? containerRef.current.getBoundingClientRect().top : 0;
                const filtersH = filtersRef.current ? filtersRef.current.getBoundingClientRect().height : 0;
                const paginationH = paginationRef.current ? paginationRef.current.getBoundingClientRect().height : 0;
                const available = window.innerHeight - top - filtersH - paginationH - 40; // padding
                setTableBodyHeight( Math.max( 200, available ) );
            } catch ( e ) {
                // ignore errors calculating layout
                // eslint-disable-next-line no-console
                console.debug( 'calculate layout error', e );
            }
        };

        calculate();
        window.addEventListener( 'resize', calculate );
        return () => window.removeEventListener( 'resize', calculate );
    }, [ isFullscreen ] );

    // viewport resize listener
    useEffect( () => {
        const handleResize = () => setViewportWidth( window.innerWidth );
        window.addEventListener( 'resize', handleResize );
        return () => window.removeEventListener( 'resize', handleResize );
    }, [] );

    // initialize columns config once
    useEffect( () => {
        let cols = loadColumnsFromStorage();
        
        // Filter out select and actions columns if user doesn't have write permissions
        // These columns should ALWAYS be hidden for read-only roles, even if forced
        if ( !canWrite ) {
            cols = cols.filter( col => ![ 'select', 'actions' ].includes( col.key ) );
        }
        
        // ensure forced columns remain visible
        const ensured = cols.map( c => {
            if ( c.forced && ![ 'select', 'actions' ].includes( c.key ) ) {
                return { ...c, visible: true };
            }
            return { ...c, visible: c.visible !== false };
        } );
        setColumns( ensured );
    }, [ canWrite ] );

    // Toggle hide navbar class on body when fullscreen
    useEffect( () => {
        if ( isFullscreen ) {
            document.body.classList.add( 'inventory-hide-navbar' );
        } else {
            document.body.classList.remove( 'inventory-hide-navbar' );
        }
    }, [ isFullscreen ] );

    // Render helpers adjusted to use dynamic columns
    const visibleColumns = columns.filter( c => c.visible !== false );

    const renderTableHeaderDynamic = () => {
        const allSelected = data.length > 0 && selectedRows.length === data.length;
        const indeterminate = selectedRows.length > 0 && selectedRows.length < data.length;

        return (
            <thead className="inventory-table-header">
                <tr>
                    {visibleColumns.map( col => {
                        if ( col.key === 'select' ) {
                            return (
                                <th key={col.key} style={{ width: col.width }}>
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={allSelected}
                                        ref={input => {
                                            if ( input ) input.indeterminate = indeterminate;
                                        }}
                                        onChange={( e ) => handleSelectAll( e.target.checked )}
                                    />
                                </th>
                            );
                        }

                        if ( col.key === 'actions' ) {
                            return <th key={col.key} style={{ width: 50 }}>Acciones</th>;
                        }

                        if ( col.key === 'image' ) {
                            return <th key={col.key} style={{ width: col.width }}>Imagen</th>;
                        }

                        // sortable headers
                        return (
                            <th
                                key={col.key}
                                className={col.sortable ? 'sortable' : ''}
                                onClick={() => col.sortable ? handleSort( col.key ) : null}
                                style={{ width: col.width, minWidth: col.minWidth }}
                            >
                                {col.label} {col.sortable ? <FontAwesomeIcon icon={getSortIcon( col.key )} /> : null}
                            </th>
                        );
                    } )}
                </tr>
            </thead>
        );
    };

    const renderTableBodyDynamic = () => {
        if ( loading ) {
            return (
                <tbody>
                    <tr>
                        <td colSpan={Math.max( 1, visibleColumns.length )} className="text-center py-5">
                            <div className="inventory-table-loading">
                                <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                                <div className="mt-2">Cargando datos...</div>
                            </div>
                        </td>
                    </tr>
                </tbody>
            );
        }

        if ( error ) {
            return (
                <tbody>
                    <tr>
                        <td colSpan={Math.max( 1, visibleColumns.length )} className="text-center py-5">
                            <div className="inventory-table-error">
                                <div>Error al cargar los datos</div>
                                <div className="text-muted mt-2">{error}</div>
                                <button
                                    className="btn btn-primary mt-3"
                                    onClick={loadData}
                                >
                                    Reintentar
                                </button>
                            </div>
                        </td>
                    </tr>
                </tbody>
            );
        }

        if ( data.length === 0 ) {
            return (
                <tbody>
                    <tr>
                        <td colSpan={Math.max( 1, visibleColumns.length )} className="text-center py-5">
                            <div className="inventory-table-empty">
                                <div>No se encontraron productos</div>
                                <div className="text-muted mt-2">
                                    Intenta ajustar los filtros o agregar nuevos productos
                                </div>
                            </div>
                        </td>
                    </tr>
                </tbody>
            );
        }

        return (
            <tbody>
                {data.map( ( item ) => {
                    const isSelected = selectedRows.includes( item.id );
                    return (
                        <tr key={item.id}>
                            {visibleColumns.map( col => {
                                if ( col.key === 'select' ) {
                                    return (
                                        <td key={`${item.id}-select`}>
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={isSelected}
                                                onChange={( e ) => handleSelectRow( item.id, e.target.checked )}
                                            />
                                        </td>
                                    );
                                }

                                if ( col.key === 'actions' ) {
                                    return (
                                        <td key={`${item.id}-actions`}>
                                            <div className="d-flex justify-content-center">
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary btn-sm"
                                                    onClick={() => onRowAction && onRowAction( 'edit', item )}
                                                    title="Editar"
                                                >
                                                    <FontAwesomeIcon icon={faEdit} />
                                                </button>
                                            </div>
                                        </td>
                                    );
                                }

                                if ( col.key === 'image' ) {
                                    return (
                                        <td key={`${item.id}-image`}>
                                            {item.image ? (
                                                <img
                                                    src={item.image}
                                                    alt={item.article}
                                                    className="inventory-table-image"
                                                    onError={e => {
                                                        e.target.onerror = null;
                                                        e.target.src = '/notfound.png';
                                                    }}
                                                />
                                            ) : (
                                                <div className="inventory-table-image-placeholder">
                                                    <FontAwesomeIcon icon={faImage} />
                                                </div>
                                            )}
                                        </td>
                                    );
                                }

                                // generic mapping
                                switch ( col.key ) {
                                    case 'id':
                                        return (
                                            <td key={`${item.id}-id`} className="clickable" onClick={() => onRowAction && onRowAction( 'view', item )} title="Click para ver detalles">
                                                {
                                                    item.qty > 0 ? (
                                                        <span className="textsuccess">{String( item.id ).padStart( 5, '0' )}</span>
                                                    ) : (
                                                        <span title='No hay stock' className="text-danger">{String( item.id ).padStart( 5, '0' )}</span>
                                                    )
                                                }
                                            </td>
                                        );
                                    case 'article':
                                        return <td key={`${item.id}-article`}>{item.article}</td>;
                                    case 'locationName':
                                        return <td key={`${item.id}-locationName`}>{item.locationName}</td>;
                                    case 'description':
                                        return (
                                            <td key={`${item.id}-description`} title={item.description} style={{ maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {item.description || '-'}
                                            </td>
                                        );
                                    case 'categoryName':
                                        return <td key={`${item.id}-categoryName`}>{item.categoryName || '-'}</td>;
                                    case 'brand':
                                        return <td key={`${item.id}-brand`}>{item.brand || '-'}</td>;
                                    case 'model':
                                        return <td key={`${item.id}-model`}>{item.model || '-'}</td>;
                                    case 'year':
                                        return <td key={`${item.id}-year`}>{item.year || '-'}</td>;
                                    case 'createdBy':
                                        return <td key={`${item.id}-createdBy`}>{item.createdBy || '-'}</td>;
                                    case 'qty':
                                        return (
                                            <td key={`${item.id}-qty`} className='text-center'>
                                                <span className={`badge ${item.qty > 0 ? 'badge-success' : 'badge-danger'}`}>
                                                    {item.qty || 0}
                                                </span>
                                            </td>
                                        );
                                    case 'price':
                                        return (
                                            <td key={`${item.id}-price`} className='text-end'>
                                                <span className="fw-bold text-success">
                                                    ${item.price ? Number( item.price ).toLocaleString() : '0'}
                                                </span>
                                            </td>
                                        );
                                    default:
                                        return <td key={`${item.id}-${col.key}`}>-</td>;
                                }
                            } )}
                        </tr>
                    );
                } )}
            </tbody>
        );
    };

    // Mobile compact pagination
    const renderMobilePagination = () => {
        const totalPages = Math.max( 1, Math.ceil( totalCount / pageSize ) );
        const disablePrev = currentPage === 0;
        const disableNext = currentPage >= totalPages - 1;
        const showTotal = viewportWidth > 400; // ocultar primero si muy estrecho
        return (
            <div className="inventory-table-pagination-mobile d-flex justify-content-between align-items-center px-2 py-1">
                <div className="d-flex align-items-center gap-2">
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        disabled={disablePrev}
                        onClick={() => !disablePrev && setCurrentPage( p => Math.max( 0, p - 1 ) )}
                        aria-label="Página anterior"
                    >
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                    <div className="small fw-semibold">
                        Página {currentPage + 1} de {totalPages}
                    </div>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        disabled={disableNext}
                        onClick={() => !disableNext && setCurrentPage( p => Math.min( totalPages - 1, p + 1 ) )}
                        aria-label="Página siguiente"
                    >
                        <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                </div>
                {showTotal && (
                    <div className="small text-muted">
                        Total: {totalCount}
                    </div>
                )}
            </div>
        );
    };

    // Mobile Filters Modal reusing existing renderFilters fields
    const MobileFiltersModal = () => {
        return (
            <div className={`modal fade ${showFiltersModal ? 'show d-block' : ''}`} tabIndex="-1" role="dialog" aria-modal={showFiltersModal}>
                <div className="modal-dialog modal-dialog-centered" role="document">
                    <div className="modal-content">
                        <div className="modal-header py-2">
                            <h6 className="modal-title mb-0">Filtros</h6>
                            <button type="button" className="btn-close" aria-label="Cerrar" onClick={() => setShowFiltersModal( false )}></button>
                        </div>
                        <div className="modal-body p-2">
                            <div className="inventory-mobile-filters-fields">
                                {renderFilters && renderFilters()}
                            </div>
                        </div>
                        <div className="modal-footer py-2 d-flex justify-content-between">
                            <button
                                type="button"
                                className="btn btn-outline-danger btn-sm d-flex align-items-center gap-1"
                                onClick={() => {
                                    try {
                                        // Intentamos limpiar inputs de filtros comunes (simple heurística: form dentro de filtros)
                                        const wrapper = document.querySelector( '.inventory-mobile-filters-fields' );
                                        if ( wrapper ) {
                                            const inputs = wrapper.querySelectorAll( 'input, select' );
                                            inputs.forEach( el => {
                                                if ( el.tagName === 'SELECT' ) el.selectedIndex = 0;
                                                else el.value = '';
                                            } );
                                        }
                                    } catch ( e ) { /* ignore */ }
                                    setShowFiltersModal( false );
                                    loadData(); // aplicar filtros vacíos
                                }}
                            >
                                <FontAwesomeIcon icon={faBroom} /> Limpiar
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary btn-sm d-flex align-items-center gap-1"
                                onClick={() => {
                                    setShowFiltersModal( false );
                                    loadData();
                                }}
                            >
                                <FontAwesomeIcon icon={faCheck} /> Aplicar
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Small modal for column config using native drag/drop
    const ColumnConfigModal = () => {
        const [editing, setEditing] = useState( JSON.parse( JSON.stringify( columns ) ) );
        const dragIndexRef = useRef( null );

        useEffect( () => {
            setEditing( JSON.parse( JSON.stringify( columns ) ) );
        }, [ showColumnsModal ] );

        const onDragStart = ( e, idx ) => {
            dragIndexRef.current = idx;
            e.dataTransfer.effectAllowed = 'move';
        };

        const onDragOver = ( e ) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
        };

        const onDrop = ( e, idx ) => {
            e.preventDefault();
            const from = dragIndexRef.current;
            const to = idx;
            if ( from === null || from === to ) return;
            const copy = [ ...editing ];
            const [ moved ] = copy.splice( from, 1 );
            copy.splice( to, 0, moved );
            dragIndexRef.current = null;
            setEditing( copy );
        };

        const toggleVisibility = ( key ) => {
            // Prevent read-only roles from enabling write-related columns
            if ( !canWrite && [ 'select', 'actions' ].includes( key ) ) {
                return;
            }
            setEditing( prev => prev.map( c => c.key === key ? { ...c, visible: c.forced ? true : !c.visible } : c ) );
        };

        const handleSave = () => {
            // Ensure forced columns are visible, but exclude select/actions for read-only roles
            let finalCols = editing.map( c => {
                if ( !canWrite && [ 'select', 'actions' ].includes( c.key ) ) {
                    return { ...c, visible: false };
                }
                return c.forced ? { ...c, visible: true } : c;
            } );
            setColumns( finalCols );
            saveColumnsToStorage( finalCols );
            setShowColumnsModal( false );
        };

        const handleRestore = () => {
            let def = DEFAULT_COLUMNS.map( c => ( { ...c, visible: c.forced ? true : true } ) );
            // Filter out write-only columns for read-only roles
            if ( !canWrite ) {
                def = def.filter( col => ![ 'select', 'actions' ].includes( col.key ) );
            }
            setEditing( def );
            setColumns( def );
            try {
                localStorage.removeItem( getStorageKey() );
            } catch ( e ) {
                // eslint-disable-next-line no-console
                console.debug( 'remove storage key failed', e );
            }
        };

        return (
            <div className={`modal fade ${showColumnsModal ? 'show d-block' : ''}`} tabIndex="-1" role="dialog" aria-modal={showColumnsModal}>
                <div className="modal-dialog modal-dialog-centered" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Configurar columnas</h5>
                            <button type="button" className="btn-close" aria-label="Cerrar" onClick={() => setShowColumnsModal( false )}></button>
                        </div>
                        <div className="modal-body">
                            <p className="small text-muted">Arrastra para reordenar. Marca para mostrar/ocultar. Los cambios se aplican al guardar.</p>
                            <ul className="list-group">
                                {editing.map( ( col, idx ) => (
                                    <li key={col.key} className="list-group-item d-flex align-items-center" draggable onDragStart={( e ) => onDragStart( e, idx )} onDragOver={( e ) => onDragOver( e, idx )} onDrop={( e ) => onDrop( e, idx )}>
                                        <span className="me-3" style={{ cursor: 'grab' }}>☰</span>
                                        <div className="me-auto">{col.label || col.key}</div>
                                        <div>
                                            <input 
                                                type="checkbox" 
                                                checked={col.visible !== false} 
                                                disabled={col.forced || ( !canWrite && [ 'select', 'actions' ].includes( col.key ) )} 
                                                onChange={() => toggleVisibility( col.key )} 
                                            />
                                        </div>
                                    </li>
                                ) )}
                            </ul>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-outline-secondary" onClick={handleRestore}>Restaurar por defecto</button>
                            <button type="button" className="btn btn-secondary" onClick={() => setShowColumnsModal( false )}>Cancelar</button>
                            <button type="button" className="btn btn-primary" onClick={handleSave}>Guardar</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={`inventory-table-container ${isFullscreen ? 'inventory-fullscreen' : ''}`} ref={containerRef}>
            {/* Top controls (filters button in mobile) */}
            <div className="inventory-table-topbar d-flex align-items-center gap-2 px-2 py-1">
                {isMobile && (
                    <>
                        <div className="flex-grow-1" style={{ flexBasis: '60%' }}>
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="Buscar productos..."
                                defaultValue={filters.search}
                                onChange={( e ) => handleSearchChange( e.target.value )}
                                aria-label="Buscar productos"
                            />
                        </div>
                        {canWrite && (
                            <button
                                type="button"
                                className="btn btn-sm btn-primary d-flex align-items-center gap-1"
                                onClick={() => onCreateProduct && onCreateProduct()}
                                aria-label="Crear producto"
                                title="Crear nuevo producto"
                            >
                                <FontAwesomeIcon icon={faPlusCircle} />
                            </button>
                        )}
                        <button
                            type="button"
                            className="btn btn-sm btn-secondary d-flex align-items-center gap-1"
                            onClick={() => {
                                setFilters( {
                                    search: '',
                                    category_id: '',
                                    brand_id: '',
                                    model_id: '',
                                    size_id: '',
                                    year: '',
                                    location_id: ''
                                } );
                                setCurrentPage( 0 );
                                loadData();
                            }}
                            aria-label="Limpiar filtros"
                            title="Limpiar filtros"
                        >
                            <FontAwesomeIcon icon={faBroom} />
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
                            onClick={() => setShowFiltersModal( true )}
                            aria-label="Mostrar filtros"
                            title="Mostrar filtros"
                        >
                            <FontAwesomeIcon icon={faFilter} />
                        </button>
                        {/* Added COG icon so the user can change columns */}
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
                            onClick={() => setShowColumnsModal( true )}
                            aria-label="Mostrar columnas"
                            title="Mostrar columnas"
                        >
                            <FontAwesomeIcon icon={faCog} />
                        </button>
                    </>
                )}
            </div>

            {/* Desktop filters */}
            {!isMobile && (
                <div className="inventory-table-filters-wrapper">
                    {renderFilters && renderFilters()}
                </div>
            )}

            <div className="inventory-table-wrapper">
                {renderToolbar()}
                <div className="inventory-table-body" ref={tableRef} style={{ height: tableBodyHeight }}>
                    <table className="table table-sm table-bordered table-condensed table-hover mb-0">
                        {renderTableHeaderDynamic()}
                        {renderTableBodyDynamic()}
                    </table>
                </div>
            </div>

            <div ref={paginationRef} className="inventory-table-pagination-wrapper">
                {isMobile ? renderMobilePagination() : renderPagination()}
            </div>

            {/* Column Config Modal */}
            {showColumnsModal ? <ColumnConfigModal /> : null}

            {/* Mobile Filters Modal */}
            {isMobile && showFiltersModal ? <MobileFiltersModal /> : null}
        </div>
    );
} );

InventoryTable.displayName = 'InventoryTable';

export default InventoryTable;
